import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  OnInit,
  Type,
} from '@angular/core';
import { SummaryListItem } from '@app/components/form/services/schema-form/schema-form-interfaces';
import { withDndGlobalLoader } from '@app/components/xux-dnd/decorators/xux-dnd-loader.decorators';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import {
  DecimalNumber,
  decimalNumber,
} from '@app/shared/common/decimal-number/decimal-number';
import { observableOf, rx } from '@app/shared/rx/rx-wrapper';
import { DmDataService } from '@app/shared/services/dm-data/dm-data.service';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue/inventory-item-venue.service';
import { NumberFormatService } from '@app/shared/services/number-format';
import { QohService } from '@app/shared/services/qoh/qoh.service';
import { SitesService } from '@app/shared/services/sites/sites.service';
import { isSiteTypeWarehouse } from '@app/shared/services/transfer/transfer.util';
import {
  IBaseEntityResponse,
  IQohInventoryItemVenue,
  IQuantityOnHandItem,
  ISite,
  ISiteResponse,
} from '@app/shared/types';
import { MAX_PAGE_SIZE } from '@config/index';
import {
  find,
  forEach,
  get,
  groupBy,
  includes,
  isEmpty,
  isFunction,
  map,
  reduce,
  filter,
} from 'lodash';
import { of } from 'rxjs';
import { Cost } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import { STAND_WORKSHEET_STATUS_VALUE } from '@app/shared/types/enums/stand-worksheet-status.enum';

@Component({
  selector: 'xvi-purchase-summary-card',
  templateUrl: './xux-inventory-value-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XuxInventoryValueCardComponent implements OnInit {
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public warehouseTotalCost: DecimalNumber = decimalNumber(0);
  public nonWarehouseTotalCost: DecimalNumber = decimalNumber(0);

  public constructor(
    public injector: Injector,
    private cdr: ChangeDetectorRef,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public summaryList = [];

  public completeSummaryList: SummaryListItem[] = [];

  public ngOnInit() {
    this.summaryList = [
      {
        status: '',
        text: this.i18nService.instant(I18n.EntityNames.Warehouse),
        count: '',
        totalCost: '',
      },
      {
        status: STAND_WORKSHEET_STATUS_VALUE.Completed,
        text: this.i18nService.instant(I18n.EntityNames.NonWarehouse),
        count: '',
        totalCost: '',
      },
    ];
    this.completeSummaryList = [...this.summaryList];
    this.getInventoryData();
  }

  @withDndGlobalLoader('inventory_value')
  private async getInventoryData(): Promise<void> {
    const params = {
      top: MAX_PAGE_SIZE,
      filter: `is_active eq true`,
      select: ['id', 'type', 'business_type'],
      include_inactive: false,
      filter_by_assigned_sites: true,
    };
    const inventoryItems = {};
    const warehouseSiteIds = [];

    return new Promise((resolve, reject) => {
      this.sitesService
        .fetchList(params, { noLoader: true })
        .pipe(
          rx.mergeMap((siteResponse: ISiteResponse) => {
            // Get warehouse site ids for calc total value
            forEach(siteResponse.items, (site: ISite) => {
              if (isSiteTypeWarehouse(site)) {
                warehouseSiteIds.push(site.id);
              }
            });

            return this.dmDataService.getSiteGroupIdList(
              map(siteResponse.items, 'id'),
            );
          }),
          rx.mergeMap((groupIds: string[]) => {
            return this.inventoryItemVenueService.batchFetchCurrentList(
              groupIds,
              {
                select: ['costs', 'site_id'],
                filter: 'is_master eq false and is_active eq true',
              },
              {
                filterMasterDocFunction: (items) => {
                  // Filter out items which preferred cost_value is 0
                  return filter(items, (item) => {
                    const costs = get(item, 'costs', []);
                    const preferredCost = find(costs, { is_preferred: true });

                    return get(preferredCost, 'cost_value', 0) !== 0;
                  });
                },
              },
            );
          }),
          rx.mergeMap((inventoryItemRes: IBaseEntityResponse) => {
            // Compose Map with site_id + entity_id as key, value is cost_value
            forEach(inventoryItemRes.items, (inventoryItem) => {
              const cost: Cost = find(inventoryItem.costs, 'is_preferred');
              inventoryItems[inventoryItem.site_id + inventoryItem.entity_id] =
                get(cost, 'cost_value', 0);
            });
            // Compose with site_id and entity_ids as params for get qoh
            const itemsGroupBySiteId = groupBy(
              inventoryItemRes.items,
              'site_id',
            );
            const qohRequestData: IQuantityOnHandItem[] = map(
              itemsGroupBySiteId,
              (inventoryItem, key) => {
                const inventoryItemEntityIds = reduce(
                  inventoryItem,
                  (current, item) => {
                    current.push(item.entity_id);

                    return current;
                  },
                  [],
                );

                return {
                  site_id: key,
                  inventory_item_entity_ids: inventoryItemEntityIds,
                };
              },
            );

            // If no items, we do not need call qoh request
            if (isEmpty(qohRequestData)) {
              return observableOf({ items: [] });
            }

            return this.qohService.batchFetchQuantityOnHand(qohRequestData);
          }),
          rx.catchError(() => {
            reject();

            return of({ items: [] });
          }),
        )
        .subscribe((qohRes) => {
          try {
            forEach(qohRes.items, (qohItem) => {
              if (includes(warehouseSiteIds, qohItem.site_id)) {
                this.warehouseTotalCost = this.calcInventoryTotalCost(
                  qohItem,
                  inventoryItems,
                  this.warehouseTotalCost,
                );
              } else {
                this.nonWarehouseTotalCost = this.calcInventoryTotalCost(
                  qohItem,
                  inventoryItems,
                  this.nonWarehouseTotalCost,
                );
              }
            });

            this.completeSummaryList = [
              {
                text: this.i18nService.instant(I18n.EntityNames.Warehouse),
                totalCost: this.numberFormatService.getNumberFormatter(true)({
                  value: this.warehouseTotalCost,
                }),
                showNegativeCls: this.warehouseTotalCost.lt(0),
              },
              {
                text: this.i18nService.instant(I18n.EntityNames.NonWarehouse),
                totalCost: this.numberFormatService.getNumberFormatter(true)({
                  value: this.nonWarehouseTotalCost,
                }),
                showNegativeCls: this.nonWarehouseTotalCost.lt(0),
              },
            ];

            this.cdr.detectChanges();
            resolve();
          } catch (error) {
            reject();
          }
        });
    });
  }

  private calcInventoryTotalCost(
    qohItem: IQohInventoryItemVenue,
    inventoryItems: object,
    totalCost: DecimalNumber,
  ): DecimalNumber {
    const count = get(qohItem, 'count', 0);
    const key = qohItem.site_id + qohItem.inventory_item_entity_id;
    const inventoryValue = decimalNumber(inventoryItems[key]).mul(count);

    return inventoryValue.add(totalCost);
  }
}
